import { walkthroughService } from "../../services/walkthroughService";
import { getBannerAlerts } from "../../utils/alertHelpers";
export type ModalType = "wizard" | "settings" | null;

export type LayoutType = "2-1" | "1-2" | "1-1" | "2-2" | "1";

export interface UISlice {
  // State
  currentModal: ModalType;
  showNotifications: boolean;
  notificationsFilter: "all" | "error" | "warning" | "info" | "critical";
  currentAlertIndex: number;
  alertsCycleStartIndex: number | null;
  alertsCycleStepCount: number;

  // Layout selector
  showLayoutSelector: boolean;
  leftLayout: LayoutType;
  rightLayout: LayoutType;

  // Actions
  openModal: (type: ModalType) => void;
  closeModal: () => void;
  openNotifications: (
    filter?: "all" | "error" | "warning" | "info" | "critical"
  ) => void;
  closeNotifications: () => void;
  setNotificationsFilter: (
    filter: "all" | "error" | "warning" | "info" | "critical"
  ) => void;
  setCurrentAlertIndex: (index: number) => void;

  // Layout selector actions
  openLayoutSelector: () => void;
  closeLayoutSelector: () => void;
  setLeftLayout: (layout: LayoutType) => void;
  setRightLayout: (layout: LayoutType) => void;
}

export const createUISlice = (set: any, get: any): UISlice => ({
  // Initial state
  currentModal: null,
  showNotifications: false,
  notificationsFilter: "all",
  currentAlertIndex: 0,
  alertsCycleStartIndex: null,
  alertsCycleStepCount: 0,
  showLayoutSelector: false,
  leftLayout: "2-1",
  rightLayout: "2-1",

  // Actions
  openModal: (type) =>
    set((state: any) => {
      if (type === "settings") {
        walkthroughService.emit("ui.opened", { target: "settings" });
      }
      return { currentModal: type };
    }),
  closeModal: () =>
    set((state: any) => {
      if (state.currentModal === "settings") {
        walkthroughService.emit("ui.closed", { target: "settings" });
      }
      return { currentModal: null };
    }),

  openNotifications: (filter = "all") =>
    set(() => {
      walkthroughService.emit("ui.opened", { target: "notifications" });
      return {
        showNotifications: true,
        notificationsFilter: filter,
      };
    }),

  closeNotifications: () =>
    set(() => {
      walkthroughService.emit("ui.closed", { target: "notifications" });
      return { showNotifications: false };
    }),

  setNotificationsFilter: (filter) => set({ notificationsFilter: filter }),

  setCurrentAlertIndex: (index) =>
    set((state: any) => {
      const total = getBannerAlerts(state.alerts || []).length;
      const prevIndex = state.currentAlertIndex;

      if (total === 0) {
        return {
          currentAlertIndex: index,
          alertsCycleStartIndex: null,
          alertsCycleStepCount: 0,
        };
      }

      let startIndex: number | null = state.alertsCycleStartIndex;
      let stepCount: number = state.alertsCycleStepCount || 0;

      if (startIndex === null) {
        startIndex = prevIndex;
        stepCount = 0;
      }

      if (index !== prevIndex) {
        stepCount += 1;
      }

      if (index === startIndex && stepCount >= total) {
        try {
          walkthroughService.emit("alerts.cycledAll", {
            target: String(total),
          });
        } catch {}
        startIndex = null;
        stepCount = 0;
      }

      return {
        currentAlertIndex: index,
        alertsCycleStartIndex: startIndex,
        alertsCycleStepCount: stepCount,
      };
    }),

  // Layout selector actions
  openLayoutSelector: () =>
    set(() => {
      walkthroughService.emit("ui.opened", { target: "layoutSelector" });
      return { showLayoutSelector: true };
    }),
  closeLayoutSelector: () =>
    set(() => {
      walkthroughService.emit("ui.closed", { target: "layoutSelector" });
      return { showLayoutSelector: false };
    }),
  setLeftLayout: (layout) => {
    const prevLeft = get().leftLayout;
    const prevRight = get().rightLayout;
    set({ leftLayout: layout });
    const { reconcileWidgetsForLayouts } = get();
    if (typeof reconcileWidgetsForLayouts === "function") {
      reconcileWidgetsForLayouts(
        get().leftLayout,
        get().rightLayout,
        prevLeft,
        prevRight
      );
    }
  },
  setRightLayout: (layout) => {
    const prevLeft = get().leftLayout;
    const prevRight = get().rightLayout;
    set({ rightLayout: layout });
    const { reconcileWidgetsForLayouts } = get();
    if (typeof reconcileWidgetsForLayouts === "function") {
      reconcileWidgetsForLayouts(
        get().leftLayout,
        get().rightLayout,
        prevLeft,
        prevRight
      );
    }
  },
});
